<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ailabDocument: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const dialogRef = ref(null);

const handleClose = () => emit('close');

const status = computed(() => props.ailabDocument?.status?.toString() || '');
const statusKey = computed(() => status.value.toUpperCase());
const statusLabel = computed(() => {
  if (!statusKey.value) return '';
  return t(`AILAB.DOCUMENTS.STATUS.${statusKey.value}`) || status.value;
});

const crawlError = computed(() => props.ailabDocument?.crawl_error || '');
const crawlErrorAt = computed(() =>
  props.ailabDocument?.crawl_error_at
    ? dynamicTime(props.ailabDocument.crawl_error_at)
    : ''
);

const firecrawl = computed(
  () => props.ailabDocument?.metadata?.firecrawl || {}
);
const rejectedPages = computed(() => {
  const list = firecrawl.value?.rejected_pages;
  return Array.isArray(list) ? list : [];
});

const copyDebugInfo = async () => {
  const payload = {
    id: props.ailabDocument?.id,
    status: status.value,
    crawl_error: crawlError.value,
    crawl_error_at: props.ailabDocument?.crawl_error_at,
    external_link: props.ailabDocument?.external_link,
    assistant_id: props.ailabDocument?.assistant?.id,
    assistant_name: props.ailabDocument?.assistant?.name,
    firecrawl: {
      rejected_count: firecrawl.value?.rejected_count,
      rejected_pages: rejectedPages.value,
    },
  };

  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    useAlert(t('AILAB.DOCUMENTS.CRAWL_DETAILS.COPY_SUCCESS') || 'Copied');
  } catch (e) {
    useAlert(t('AILAB.DOCUMENTS.CRAWL_DETAILS.COPY_ERROR') || 'Copy failed');
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('AILAB.DOCUMENTS.CRAWL_DETAILS.TITLE')"
    :description="t('AILAB.DOCUMENTS.CRAWL_DETAILS.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="3xl"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="flex gap-2 items-center justify-between">
        <div class="flex flex-col gap-1">
          <div class="text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">
              {{
                t('AILAB.DOCUMENTS.CRAWL_DETAILS.STATUS_LABEL') || 'Status'
              }}:
            </span>
            {{ statusLabel || '-' }}
          </div>
          <div v-if="crawlError" class="text-sm text-n-ruby-9">
            <span class="font-medium">
              {{ t('AILAB.DOCUMENTS.ERROR_LABEL') || 'Crawl error' }}:
            </span>
            {{ crawlError }}
            <span v-if="crawlErrorAt" class="text-n-slate-11">
              ({{ crawlErrorAt }})
            </span>
          </div>
        </div>
        <Button
          icon="i-lucide-copy"
          color="slate"
          variant="outline"
          size="sm"
          :label="
            t('AILAB.DOCUMENTS.CRAWL_DETAILS.COPY_BUTTON') ||
            'Copy debug info'
          "
          @click="copyDebugInfo"
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-sm font-medium text-n-slate-12">
          {{
            t('AILAB.DOCUMENTS.CRAWL_DETAILS.REJECTED_PAGES_TITLE') ||
            'Rejected pages'
          }}
          <span class="text-n-slate-11"> ({{ rejectedPages.length }}) </span>
        </div>

        <div v-if="!rejectedPages.length" class="text-sm text-n-slate-11">
          {{
            t('AILAB.DOCUMENTS.CRAWL_DETAILS.REJECTED_PAGES_EMPTY') ||
            'No rejected pages'
          }}
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="(p, idx) in rejectedPages"
            :key="`${p?.url || ''}-${idx}`"
            class="p-3 rounded-lg bg-n-alpha-2"
          >
            <div class="text-sm text-n-slate-12 break-words">
              <span class="font-medium">URL:</span> {{ p?.url || '-' }}
            </div>
            <div class="text-sm text-n-slate-11 break-words">
              <span class="font-medium text-n-slate-12">Reason:</span>
              {{ p?.reason || '-' }}
              <span v-if="p?.at" class="text-n-slate-11">
                ({{ dynamicTime(p.at) }})
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
